/**
 * Allys Search Bar
 * 
 * Enhanced search bar with Allys theme styling and micro-interactions
 * Features modern aesthetic with gradients, animations, and beautiful hover states
 */

'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface AllysSearchBarProps {
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current search value
   */
  value?: string;
  /**
   * Callback when search changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback when search is submitted
   */
  onSubmit?: (value: string) => void;
  /**
   * Debounce delay in ms (only used in controlled mode)
   */
  debounceMs?: number;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * AllysSearchBar - Modern search with Allys theme styling
 */
export function AllysSearchBar({
  placeholder = 'Search...',
  value: controlledValue,
  onChange,
  onSubmit,
  debounceMs = 300,
  className = '',
}: AllysSearchBarProps) {
  const [displayValue, setDisplayValue] = useState(controlledValue ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const lastEmittedRef = useRef(controlledValue ?? '');

  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== lastEmittedRef.current) {
      setDisplayValue(controlledValue);
      lastEmittedRef.current = controlledValue;
    }
  }, [controlledValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback((newValue: string) => {
    setDisplayValue(newValue);

    if (controlledValue === undefined) {
      onChange?.(newValue);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = newValue;
      onChange?.(newValue);
    }, debounceMs);
  }, [controlledValue, onChange, debounceMs]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastEmittedRef.current = displayValue;
    onChange?.(displayValue);
    onSubmit?.(displayValue);
  };

  const handleClear = () => {
    setDisplayValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastEmittedRef.current = '';
    onChange?.('');
  };

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`}>
      <div 
        className={`
          relative overflow-hidden rounded-xl border-2 transition-all duration-300
          ${isFocused 
            ? 'border-[#243462] shadow-lg shadow-[#243462]/10 ring-4 ring-[#BBA339]/10' 
            : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md'
          }
        `}
      >
        {/* Background gradient effect */}
        <div className={`
          absolute inset-0 bg-gradient-to-r from-[#243462]/5 via-transparent to-[#BBA339]/5 opacity-0 transition-opacity duration-300
          ${isFocused ? 'opacity-100' : ''}
        `} />
        
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
          <motion.svg
            className={`h-5 w-5 transition-colors duration-300 ${isFocused ? 'text-[#243462]' : 'text-gray-400'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            animate={isFocused ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </motion.svg>
        </div>
        
        {/* Input */}
        <input
          type="text"
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="
            relative block w-full bg-white pl-12 pr-12 py-3.5 text-gray-900 placeholder-gray-400
            focus:outline-none font-medium text-sm font-inter
          "
        />
        
        {/* Clear Button */}
        <AnimatePresence>
          {displayValue && (
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-4 flex items-center z-10 group"
            >
              <motion.div
                whileHover={{ rotate: 90, scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-50 flex items-center justify-center transition-colors"
              >
                <svg
                  className="h-4 w-4 text-gray-500 group-hover:text-red-600 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.div>
            </motion.button>
          )}
        </AnimatePresence>
        
        {/* Animated border accent */}
        {isFocused && (
          <motion.div
            layoutId="searchBarAccent"
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#243462] via-[#BBA339] to-[#E4CE4A]"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            exit={{ scaleX: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </div>
    </form>
  );
}

